import { useRef, useState, useEffect } from "react";
import { Paperclip, Send, Smile, X, FileText, Image as ImageIcon, Music, Video, LayoutTemplate } from "lucide-react";
import { Button } from "../../tailwind/button";
import { Textarea } from "../../tailwind/textarea";
import { cn } from "../../../lib/utils";
import ChatEmojiPicker from "./ChatEmojiPicker";
import { useChatEmojiInsert } from "./utils/useChatEmojiInsert";
import { waBtn } from "../../../pages/whatsapp/utils/whatsappUiTokens";

export default function ChatComposer({
  messageInput,
  onMessageInputChange,
  onSend,
  onMediaSelect,
  selectedMedia,
  onMediaRemove,
  onTemplateClick,
  isLoading = false,
  canEdit = true,
  isPast24Hours = false,
  templateOnlyReason = null,
}) {
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const isInputDisabled = !canEdit || isLoading || isPast24Hours;
  const canSend = Boolean(messageInput.trim() || selectedMedia);

  const handleEmojiSelect = useChatEmojiInsert({
    messageInput,
    onMessageInputChange,
    textareaRef,
  });

  useEffect(() => {
    if (!isEmojiPickerOpen) return;

    const handleClickOutside = (event) => {
      if (emojiPickerRef.current?.contains(event.target)) return;
      setIsEmojiPickerOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isEmojiPickerOpen]);

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      onMediaSelect?.(file);
    }
  };

  const handleRemoveMedia = () => {
    onMediaRemove?.();
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const getMediaIcon = () => {
    if (!selectedMedia) return null;
    const type = selectedMedia.file.type;
    if (type.startsWith("image/")) return <ImageIcon className="h-4 w-4" />;
    if (type.startsWith("audio/")) return <Music className="h-4 w-4" />;
    if (type.startsWith("video/")) return <Video className="h-4 w-4" />;
    return <FileText className="h-4 w-4" />;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isInputDisabled && canSend) {
        onSend();
      }
    }
  };

  return (
    <div className="p-3 bg-[#f0f2f5] dark:bg-slate-900 shrink-0">
      {selectedMedia && (
        <div className="mb-2 p-2 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="text-gray-500 dark:text-slate-400">
              {getMediaIcon()}
            </div>
            <p className="text-sm text-gray-700 dark:text-slate-300 truncate">
              {selectedMedia.file.name}
            </p>
          </div>
          <button
            onClick={handleRemoveMedia}
            className="shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded transition-colors"
          >
            <X className="h-4 w-4 text-gray-500 dark:text-slate-400" />
          </button>
        </div>
      )}
      <div className="flex items-end gap-2 bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-2">
        <div className="flex gap-1 pb-1">
          <div className="relative shrink-0" ref={emojiPickerRef}>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              disabled={isInputDisabled}
              aria-label="Insert emoji"
              aria-expanded={isEmojiPickerOpen}
              onClick={() => setIsEmojiPickerOpen((open) => !open)}
              className={cn(
                "h-8 w-8 text-gray-500 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full shrink-0 disabled:opacity-50",
                isEmojiPickerOpen && "bg-gray-100 dark:bg-slate-800"
              )}
            >
              <Smile className="h-5 w-5" />
            </Button>
            {isEmojiPickerOpen && (
              <ChatEmojiPicker onEmojiSelect={handleEmojiSelect} />
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onTemplateClick}
            disabled={!canEdit || isLoading}
            className="h-8 w-8 text-gray-500 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full shrink-0 disabled:opacity-50"
          >
            <LayoutTemplate className="h-5 w-5" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleAttachmentClick}
            disabled={isInputDisabled}
            className="h-8 w-8 text-gray-500 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-full shrink-0 disabled:opacity-50"
          >
            <Paperclip className="h-5 w-5" />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileChange}
            className="hidden"
            accept="image/*,audio/*,video/*,.pdf,.txt,.doc,.docx"
            disabled={isInputDisabled}
          />
        </div>


        <Textarea
          ref={textareaRef}
          value={messageInput}
          onChange={(event) => onMessageInputChange(event.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsEmojiPickerOpen(false)}
          placeholder={
            !canEdit
              ? "View only"
              : isPast24Hours
              ? templateOnlyReason === 'NEW_CONVERSATION'
                ? "Only templates allowed for new conversation"
                : "Only templates allowed after 24h"
              : "Type a message..."
          }
          className="flex-1 max-h-32 min-h-[40px] resize-none outline-none py-2.5 px-2 bg-transparent text-[15px] text-gray-900 dark:text-slate-100 placeholder:text-gray-500 dark:placeholder:text-slate-500 border-0 shadow-none focus-visible:ring-0 disabled:opacity-50"
          rows={1}
          disabled={isInputDisabled}
        />

        <div className="pb-1 pr-1">
          <Button
            type="button"
            size="icon"
            onClick={onSend}
            disabled={isInputDisabled || !canSend}
            className={cn(
              "h-10 w-10 rounded-full shrink-0 transition-colors",
              canSend && !isLoading
                ? waBtn.primary
                : "bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-300"
            )}
          >
            {isLoading ? (
              <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="h-4 w-4 ml-0.5" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
